// Health HUD (UI-4): a row of 10 hearts (2 hp each), full/half/empty, plus gold
// absorption overlay. Hidden in creative/spectator (no health bar). The heart
// sprite is painted once to a canvas (pixel-art, no assets). Client-only (ENG-1).
import { h } from '../h';
import { modeCaps } from '../../../sim/game/modes';
import type { Game } from '../../boot';

const SIZE = 9;

/** Paint a heart into a SIZE×SIZE canvas: kind 0 empty, 1 half, 2 full, 3 absorb. */
function heartURL(kind: number): string {
  const cvs = document.createElement('canvas');
  cvs.width = SIZE; cvs.height = SIZE;
  const ctx = cvs.getContext('2d')!;
  // Classic 9×9 heart mask.
  const mask = [
    '.##.##.',
    '#######',
    '#######',
    '.#####.',
    '..###..',
    '...#...',
  ];
  const fill = kind === 0 ? '#3a1416' : kind === 3 ? '#f2b63a' : '#e23b3b';
  const empty = '#3a1416';
  for (let y = 0; y < mask.length; y++) {
    for (let x = 0; x < mask[y]!.length; x++) {
      if (mask[y]![x] !== '#') continue;
      const half = kind === 1 && x >= Math.ceil(mask[y]!.length / 2);
      ctx.fillStyle = kind === 0 || half ? empty : fill;
      ctx.fillRect(x + 1, y + 1, 1, 1);
    }
  }
  return cvs.toDataURL();
}

export class Hearts {
  readonly el: HTMLElement;
  private readonly cells: HTMLElement[] = [];
  private readonly kind: number[] = new Array(10).fill(-1);
  private readonly urls = [heartURL(0), heartURL(1), heartURL(2), heartURL(3)];
  private lastShow: boolean | null = null;

  constructor() {
    this.el = h('div', { class: 'bb-hearts' });
    for (let i = 0; i < 10; i++) {
      const c = h('div', { class: 'bb-stat-icon' });
      this.cells.push(c);
      this.el.append(c);
    }
  }

  update(game: Game): void {
    const p = game.playerState;
    const show = modeCaps(p.gameMode).survivalHunger;
    if (show !== this.lastShow) { this.el.style.display = show ? 'flex' : 'none'; this.lastShow = show; }
    if (!show) return;
    const hp = Math.max(0, Math.round(p.health));
    const abs = Math.round(p.absorption);
    for (let i = 0; i < 10; i++) {
      const heartHp = hp - i * 2;
      let kind = heartHp >= 2 ? 2 : heartHp === 1 ? 1 : 0;
      if (i * 2 < abs) kind = 3; // absorption hearts take priority visually
      if (kind === this.kind[i]) continue; // diff — only touch the DOM on change
      this.kind[i] = kind;
      this.cells[i]!.style.backgroundImage = `url(${this.urls[kind]})`;
    }
  }
}
